<template>
  <div v-if="warranty">
    <WarrantyCustomerInfoSection
      :warranty="warranty"
      :warranty-service-options="warrantyServiceOptions"
      :is-edit-mode="isEditMode"
      :can-update-service-type="canUpdateServiceType"
      @update-field="updateField"
      @customer-found="onCustomerFound"
      @customer-cleared="onCustomerCleared"
    />

    <WarrantyProductWarrantyInfoSection
      :warranty="warranty"
      :warranty-product="warranty.warrantyProduct || {}"
      :option-stores="optionStores"
      :inspected-by-employee-name="inspectedByEmployeeName"
      :is-edit-mode="isEditMode"
      :is-create-from-bill="isCreateFromBill"
      :allow-package-dismiss="allowPackageDismiss"
      :dp-configs="dpConfigs"
      @update-field="updateField"
      @product-selected="onProductSelected"
      @select-store="onSelectStore"
      @imei-selected="onImeiSelected"
      @employee-selected="onInspectedByEmployeeSelected"
      @clear-package="onClearPackage"
    />

    <!-- Accompanying Items -->
    <b-row class="mb-3 mt-4 align-items-end">
      <b-col>
        <b-form-group class="mb-0">
          <label class="text-dark font-size-h5 font-weight-bold mb-2 d-block"> Kèm theo </label>
          <div class="d-flex flex-wrap">
            <b-form-checkbox
              v-for="equipment in accompanyingEquipments"
              :key="equipment.id"
              :checked="
                warranty.accompanyingItems && warranty.accompanyingItems.includes(equipment.id)
              "
              @change="toggleAccompanyingItem(equipment.id, $event)"
              :disabled="!equipment.isActive"
              class="mr-3 mb-2"
            >
              {{ equipment.name }}
            </b-form-checkbox>
          </div>
        </b-form-group>
      </b-col>
      <b-col cols="3">
        <b-form-group class="mb-0">
          <label>Báo giá</label>
          <BInputNumber
            :value="warranty.estimatedCost"
            @valueChange="updateField('estimatedCost', $event)"
            placeholder="Nhập báo giá"
            size="sm"
          />
        </b-form-group>
      </b-col>
    </b-row>

    <!-- Issue and Notes -->
    <b-row class="mb-3 mt-4">
      <b-col>
        <p class="text-dark font-size-h5 font-weight-bold mb-3">Mô tả và ghi chú</p>
      </b-col>
    </b-row>
    <b-row>
      <b-col cols="4">
        <b-form-group>
          <label>Mô tả lỗi</label>
          <b-form-textarea
            :value="warranty.issueDescription"
            @input="updateField('issueDescription', $event)"
            placeholder="Nhập mô tả lỗi"
            rows="5"
          ></b-form-textarea>
        </b-form-group>
      </b-col>
      <b-col cols="4">
        <b-form-group>
          <label>Ngoại quan</label>
          <b-form-textarea
            :value="warranty.externalNote"
            @input="updateField('externalNote', $event)"
            placeholder="Nhập ghi chú ngoại"
            rows="5"
          ></b-form-textarea>
        </b-form-group>
      </b-col>
      <b-col cols="4">
        <b-form-group>
          <label>Ghi chú nội bộ</label>
          <b-form-textarea
            :value="warranty.internalNote"
            @input="updateField('internalNote', $event)"
            placeholder="Nhập ghi chú nội bộ"
            rows="5"
          ></b-form-textarea>
        </b-form-group>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import timeUtils from '@/utils/date'
import WarrantyCustomerInfoSection from './WarrantyCustomerInfoSection.vue'
import WarrantyProductWarrantyInfoSection from './WarrantyProductWarrantyInfoSection.vue'
import BInputNumber from '@/view/base/bootstrap/BInputNumber.vue'

export default {
  name: 'WarrantyReceptionSection',
  props: {
    value: {
      type: Object,
      required: true,
      default: () => ({}),
    },
    warrantyServiceOptions: {
      type: Array,
      default: () => [],
    },
    accompanyingEquipments: {
      type: Array,
      default: () => [],
    },
    optionStores: {
      type: Array,
      default: () => [],
    },
    inspectedByEmployeeName: {
      type: String,
      default: '',
    },
    isEditMode: {
      type: Boolean,
      default: false,
    },
    isCreateFromBill: {
      type: Boolean,
      default: false,
    },
    allowPackageDismiss: {
      type: Boolean,
      default: true,
    },
    canUpdateServiceType: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    BInputNumber,
    WarrantyCustomerInfoSection,
    WarrantyProductWarrantyInfoSection,
  },
  data() {
    return {
      dpConfigs: {
        date: timeUtils.DP_CONFIG.date,
        datetime: timeUtils.DP_CONFIG_DATETIME.date,
      },
    }
  },
  computed: {
    warranty() {
      return this.value
    },
  },
  methods: {
    updateField(field, value) {
      // Only emit if value actually changed
      if (this.warranty[field] === value) return

      const updatedWarranty = { ...this.warranty, [field]: value }
      this.$emit('input', updatedWarranty)
    },
    onProductSelected({ productName, productId }) {
      this.$emit('input', {
        ...this.warranty,
        productName: productName ?? this.warranty.productName,
        productId: productId ?? this.warranty.productId,
      })
    },
    onCustomerFound(customerData) {
      const updates = {}

      // Chỉ update nếu field hiện tại trống
      if (customerData.customerName && !this.warranty.customerName) {
        updates.customerName = customerData.customerName
      }
      if (customerData.customerEmail && !this.warranty.customerEmail) {
        updates.customerEmail = customerData.customerEmail
      }
      if (customerData.customerAddress && !this.warranty.customerAddress) {
        updates.customerAddress = customerData.customerAddress
      }
      if (customerData.customerId && !this.warranty.customerId) {
        updates.customerId = customerData.customerId
      }

      if (Object.keys(updates).length > 0) {
        const updatedWarranty = { ...this.warranty, ...updates }
        this.$emit('input', updatedWarranty)
      }
    },
    onCustomerCleared() {
      // Clear customer info if phone is empty or too short
      this.updateField('customerName', '')
      this.updateField('customerEmail', '')
      this.updateField('customerAddress', '')
    },
    async onImeiSelected(productData) {
      const updates = {
        imeiCode: productData.imeiCode,
        productName: productData.productName,
      }

      if (productData.productId) {
        updates.productId = productData.productId
      }

      const updatedWarranty = { ...this.warranty, ...updates }
      this.$emit('input', updatedWarranty)
      this.$emit('imei-selected', productData)
    },
    onSelectStore(node) {
      const storeId = node && node.id ? node.id : null
      const storeName = node && node.label ? node.label : null

      // Only emit if value actually changed
      if (this.warranty.storeId === storeId && this.warranty.storeName === storeName) return

      this.$emit('input', {
        ...this.warranty,
        storeId,
        storeName,
      })
    },
    onInspectedByEmployeeSelected(employee) {
      const employeeId = employee ? employee.id : null

      this.$emit('employee-selected', employee)

      if (this.warranty.inspectedByEmployeeId === employeeId) return

      this.$emit('input', {
        ...this.warranty,
        inspectedByEmployeeId: employeeId,
      })
    },
    onClearPackage() {
      this.$emit('input', {
        ...this.warranty,
        warrantyProduct: {},
        warrantyProductId: null,
      })
    },
    toggleAccompanyingItem(equipmentId, checked) {
      const currentItems = this.warranty.accompanyingItems || []
      let updatedItems
      if (checked) {
        updatedItems = [...currentItems, equipmentId]
      } else {
        updatedItems = currentItems.filter((id) => id !== equipmentId)
      }
      this.updateField('accompanyingItems', updatedItems)
    },
  },
}
</script>
